export const NON_NEGATIVE_EPSILON = 1e-10;

export type PointLike =
  | [number | null | undefined, number | null | undefined]
  | { x?: number | null | undefined; y?: number | null | undefined }
  | null
  | undefined;

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const normalizePoint = (point: PointLike): { x: number; y: number } | null => {
  if (Array.isArray(point)) {
    const numericX = toFiniteNumber(point[0]);
    const numericY = toFiniteNumber(point[1]);
    if (numericX !== null && numericY !== null) {
      return { x: numericX, y: numericY };
    }
    return null;
  }

  if (point && typeof point === 'object') {
    const numericX = toFiniteNumber((point as { x?: unknown }).x);
    const numericY = toFiniteNumber((point as { y?: unknown }).y);
    if (numericX !== null && numericY !== null) {
      return { x: numericX, y: numericY };
    }
  }

  return null;
};

export const summarizeSeriesPoints = (points: PointLike[], epsilon = NON_NEGATIVE_EPSILON) => {
  let invalidCount = 0;
  let nonFiniteCount = 0;
  const normalized: Array<{ x: number; y: number }> = [];

  points.forEach(point => {
    const normalizedPoint = normalizePoint(point);
    if (!normalizedPoint) {
      invalidCount += 1;
      const candidate = Array.isArray(point)
        ? point?.[1]
        : point && typeof point === 'object'
          ? (point as { y?: unknown }).y
          : null;
      const numericCandidate = toFiniteNumber(candidate);
      if (numericCandidate === null) {
        nonFiniteCount += 1;
      }
      return;
    }

    normalized.push(normalizedPoint);
  });

  const yValues = normalized.map(({ y }) => y);
  const minY = yValues.length ? Math.min(...yValues) : null;
  const maxY = yValues.length ? Math.max(...yValues) : null;
  const negativeCount = yValues.filter(value => value < 0).length;
  const tinyNegativeCount = yValues.filter(value => value < 0 && value > -epsilon).length;

  return {
    totalCount: points.length,
    finiteCount: normalized.length,
    invalidCount,
    nonFiniteCount,
    minY,
    maxY,
    negativeCount,
    tinyNegativeCount,
    firstPoints: normalized.slice(0, 3),
    lastPoints: normalized.slice(-3),
  };
};

const collectVisibleFiniteValues = (
  seriesCollection: Array<{ data?: PointLike[]; visible?: boolean } | null | undefined>,
) => {
  const values: number[] = [];

  seriesCollection.forEach(series => {
    if (!series || series.visible === false) {
      return;
    }

    const data = Array.isArray(series.data) ? series.data : [];
    data.forEach(point => {
      const normalizedPoint = normalizePoint(point);
      if (normalizedPoint) {
        values.push(normalizedPoint.y);
      }
    });
  });

  return values;
};

export const deriveNonNegativeAxisFloor = (
  seriesCollection: Array<{ data?: PointLike[]; visible?: boolean } | null | undefined>,
  epsilon = NON_NEGATIVE_EPSILON,
) => {
  const values = collectVisibleFiniteValues(seriesCollection);
  if (!values.length) {
    return { floor: undefined as number | undefined, minValue: null as number | null };
  }

  const minValue = values.reduce((acc, value) => Math.min(acc, value), Number.POSITIVE_INFINITY);
  if (minValue >= -epsilon) {
    return { floor: 0, minValue };
  }

  return { floor: undefined, minValue };
};

export const deriveAxisLimits = (
  values: Array<number | null | undefined>,
  paddingRatio = 0.05,
): { min?: number; max?: number } => {
  const finiteValues = values.filter((value): value is number => Number.isFinite(value as number));
  if (!finiteValues.length) {
    return {};
  }

  let min = Math.min(...finiteValues);
  let max = Math.max(...finiteValues);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return {};
  }

  if (min === max) {
    const pad = Math.abs(min || 1) * paddingRatio || paddingRatio;
    min -= pad;
    max += pad;
  } else {
    const span = Math.abs(max - min);
    const pad = span * paddingRatio;
    min -= pad;
    max += pad;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
    return {};
  }

  return { min, max };
};

/** Last visible X window (e.g. after mouse-wheel zoom) so we can restore after React re-applies chart options. */
export type SavedXExtents = {
  min: number;
  max: number;
};

export function readFiniteXExtentsFromAxis(
  axis: { getExtremes: () => { min?: unknown; max?: unknown } } | null | undefined,
): SavedXExtents | null {
  if (!axis || typeof axis.getExtremes !== 'function') {
    return null;
  }
  const { min, max } = axis.getExtremes();
  const nMin = typeof min === 'number' ? min : Number(min);
  const nMax = typeof max === 'number' ? max : Number(max);
  if (!Number.isFinite(nMin) || !Number.isFinite(nMax) || nMin >= nMax) {
    return null;
  }
  return { min: nMin, max: nMax };
}

export function xExtentsDiffer(current: SavedXExtents, saved: SavedXExtents, toleranceRatio = 0.0005): boolean {
  const span = Math.max(saved.max - saved.min, 1e-9);
  const tol = span * toleranceRatio;
  return Math.abs(current.min - saved.min) > tol || Math.abs(current.max - saved.max) > tol;
}

/** Only persist X extents from user-driven axis changes (not programmatic preset apply). */
export function shouldPersistUserXExtentsFromEvent(
  event: { trigger?: string } | null | undefined,
  options?: { applyingRangePreset?: boolean },
): boolean {
  if (options?.applyingRangePreset) {
    return false;
  }
  const trigger = event?.trigger;
  if (typeof trigger !== 'string' || !trigger) {
    return false;
  }
  return (
    trigger === 'zoom' ||
    trigger === 'pan' ||
    trigger === 'navigator' ||
    trigger === 'scrollbar' ||
    trigger === 'mousewheel' ||
    trigger === 'touchpinch' ||
    trigger === 'touchmove'
  );
}

/** Prefer event min/max from Highcharts `afterSetExtremes`, fall back to axis.getExtremes(). */
export function pickXExtentsFromAfterSetExtremesEvent(event: any): SavedXExtents | null {
  if (!event) {
    return null;
  }
  const evMin = event.min;
  const evMax = event.max;
  if (Number.isFinite(evMin) && Number.isFinite(evMax) && evMin < evMax) {
    return { min: Number(evMin), max: Number(evMax) };
  }
  return readFiniteXExtentsFromAxis(event.target);
}
